/*
 * CONFIDENTIAL AND PROPRIETARY
 *
 * Copyright (c) 2003 - 2021 Levels Beyond
 * Copyright (c) 2021 - 2023 Signiant (by acquisition)
 * Portions Copyright (c) 2023 - 2026 Quickplay Media
 *
 * ALL RIGHTS RESERVED
 *
 * This software is licensed to Quickplay Media under a formal
 * license agreement. Unauthorized use, reproduction, or
 * distribution is strictly prohibited.
 */

/**
 * Created by jessenelson on 8/3/16.
 */
'use strict';
angular.module('prism-ui.workflows').service('WorkflowsService',
	function (_, $q, EnumService, WorkflowExecutionResourceFactory, PermissionsService, PaginationService) {
		var self = this;
		self.expandedWorkflowsByTreeLevel = []; // 2D array. 1st == treeLevel, 2nd == workflows in that level (sorted by id)
        self.permissions = {};

		var WORKFLOWS_VIEW_STATE = 'DASHBOARD_WORKFLOW_VIEW';
        var RESUMEABLE_STATUSES = ['EXECUTING', 'STALLED', 'PAUSED', 'WAITING', 'CREATED'];
        var PAUSEABLE_STATUSES = ['EXECUTING', 'STALLED', 'QUEUED'];

        //============================//
		// Expanded Workflow Tracking //
        //============================//

        self.addItemToExpandedWorkflows = function (workflow) {
            var workflowsInTreeLevelById = self.expandedWorkflowsByTreeLevel[workflow.$$treeLevel];
            if (!workflowsInTreeLevelById) {
                workflowsInTreeLevelById = {};
                self.expandedWorkflowsByTreeLevel[workflow.$$treeLevel] = workflowsInTreeLevelById;
            }
            workflowsInTreeLevelById[workflow.id] = workflow;
        };

		self.removeItemFromExpandedWorkflows = function (workflowId) {
			for (var i in self.expandedWorkflowsByTreeLevel)  {
				var workflowsInTreeLevelById = self.expandedWorkflowsByTreeLevel[i];
				if (workflowId in workflowsInTreeLevelById) {
                    delete workflowsInTreeLevelById[workflowId];
                    return;
				}
			}
		};

        //====================//
        // Workflow Retrieval //
        //====================//

        var addDisplayedStatus = function(workflow) {
            workflow.displayedStatus = workflow.status;
            if (workflow.hasOwnProperty('errorMessage') && workflow.errorMessage.length > 0) {
                workflow.displayedStatus += ' | ' + workflow.errorMessage;
            } else if (workflow.hasOwnProperty('infoMessage') && workflow.infoMessage.length > 0) {
                workflow.displayedStatus += ' | ' + workflow.infoMessage;
            }
        };

        var getQueryOpts = function(pagingOptions, searchCriteria) {
            var offset = pagingOptions.pageSize * (pagingOptions.currentPage - 1);
            var queryOpts = {
                fetchOffset: offset,
                fetchLimit: pagingOptions.pageSize
            };
            angular.extend(queryOpts, searchCriteria);
            return queryOpts;
        };

		self.getWorkflows = function (pagingOptions, searchCriteria) {
		    var queryOpts = getQueryOpts(pagingOptions, searchCriteria);
			return WorkflowExecutionResourceFactory.query(queryOpts, function (data, headers) {
				PaginationService.parseResponseHeaders(headers());
				angular.forEach(data, function (workflow) {
                    // ensure each workflow has a displayable
                    // status message
                    addDisplayedStatus(workflow);
					workflow.$$treeLevel = 0;
				});
				return data;
			}).$promise.then(function(data) {
                return _.orderBy(data, ['dateCreated', 'id'], ['desc', 'desc']);
            });
		};

		self.retrieveAllExpandedSubflows = function() {
            var subflowPromisesByParentId = {};
            var parentIds = []; // Guarantees that workflow's id is inserted before any of it's descendant's ids
            _.forEach(self.expandedWorkflowsByTreeLevel, function (workflowsInTreeLevelById, treeLevel) {
                // Iterates through each workflow to make it easier to identify subflows by parentId
                _.forEach(workflowsInTreeLevelById, function (workflow) {
                    parentIds.push(workflow.id);
                    var subflowsPromise = self.retrieveSubflowData([workflow], treeLevel + 1)
                        // Only one workflow's subflows were retrieved, so flatten out the object
                        .then(function(subflowsByParentId) {
                            return _.values(subflowsByParentId)[0];
                        });
                    subflowPromisesByParentId[workflow.id] = subflowsPromise;
                });
            });
            return $q.all({
                subflowsByParentId: $q.all(subflowPromisesByParentId),
                parentIterationOrder: parentIds
            });
        };

		self.retrieveSubflowData = function (workflows, subflowsTreeLevel) {
			var subflowsPromisesByParentId = {};
			_.forEach(workflows, function (workflow) {
                subflowsPromisesByParentId[workflow.id] = WorkflowExecutionResourceFactory
                    .getSubflowExecutions({id: workflow.id})
                    .$promise
                    .then(function (subflows) {
                        // ensure each subflow has a displayable
                        // status message
                        _.forEach(subflows, function (subflow) {
                            addDisplayedStatus(subflow);
                        });
                        return subflows;
                    });
            });

			return $q.all(subflowsPromisesByParentId).then(function (subflowsByParentId) {
			    _.forEach(workflows, function(workflow) {
                    subflowsByParentId[workflow.id] = _.orderBy(subflowsByParentId[workflow.id], ['dateCreated', 'id'], ['desc', 'desc']);
                    workflow.subflows = subflowsByParentId[workflow.id];
                    _.forEach(workflow.subflows, function (subflow) {
                        subflow.$$treeLevel = subflowsTreeLevel;
                    });
                });
                return subflowsByParentId;
            });
		};

        //===================//
        // Permission Checks //
        //===================//

        self.canReadWorkflows = function() {
            return self.permissions.read;
        };

        var doesWorkflowHaveParent = function (workflow) {
            return !!workflow.parentExecutionId;
        };

        var isWorkflowResumeable = function (workflow) {
            return !doesWorkflowHaveParent(workflow) && RESUMEABLE_STATUSES.indexOf(workflow.status) >= 0;
        };

        var isWorkflowPauseable = function (workflow) {
            return PAUSEABLE_STATUSES.indexOf(workflow.status) >= 0;
        };

        self.canDoWorkflowAction = function(action, selectedWorkflows) {
            if (!selectedWorkflows || selectedWorkflows.length < 1) {
                return false;
            }
            if (action !== 'view' && !self.permissions.write) {
                return false;
            }

            switch (action) {
                case 'view':
                    return selectedWorkflows.length === 1;

                case 'resume':
                    if (!_.every(selectedWorkflows, isWorkflowResumeable)) {
                        return false;
                    }
                    break;

                case 'pause':
                    if (!_.every(selectedWorkflows, isWorkflowPauseable)) {
                        return false;
                    }
                    break;

                case 'abandon':
                    if (_.some(selectedWorkflows, doesWorkflowHaveParent)) {
                        return false;
                    }
                    break;
            }
            return true;
        };

        //=======================//
        // Workflow Modification //
        //=======================//

		self.pause = function(workflowList) {
			var promises = [];

			for (var i in workflowList) {
				promises.push(WorkflowExecutionResourceFactory.stop({id: workflowList[i].id}).$promise);
			}

			return $q.all(promises);
		};

		self.resume = function(workflowList) {
			var promises = [];

			for (var i in workflowList) {
				promises.push(WorkflowExecutionResourceFactory.resume({id: workflowList[i].id}).$promise);
			}

			return $q.all(promises);
		};

		self.abandon = function(workflowList) {
			var promises = [];

			for (var i in workflowList) {
				workflowList[i].status = 'ABANDONED';
				promises.push(WorkflowExecutionResourceFactory.abandon({id: workflowList[i].id}).$promise);
			}

			return $q.all(promises);
		};

        //=================//
        // Lifecycle Hooks //
        //=================//

		self.initialize = function() {
            var canRead = PermissionsService.canRead(WORKFLOWS_VIEW_STATE);
            var canWrite = PermissionsService.canWrite(WORKFLOWS_VIEW_STATE);
            let enums = EnumService.queryForEnums();
            return $q.all({
                read: canRead,
                write: canWrite,
                enums: enums
            }).then(function (permissions) {
                self.permissions = permissions;
            });
        };

	});
